import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Alert,
  CircularProgress,
  Pagination,
  List,
  ListItem,
  Checkbox,
  FormControlLabel,
  Collapse,
  Card,
  CardContent,
  Tab,
  Tabs,
  Grid,
  styled,
  Badge,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  PersonAdd as PersonAddIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  RemoveRedEye as ViewerIcon,
  Edit as EditorIcon,
  FilterList as FilterListIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Storage as StorageIcon,
  Hub as HubIcon,
  Settings as SettingsIcon,
  Chat as ChatIcon,
  LibraryBooks as LibraryBooksIcon,
  AccountTree as AccountTreeIcon,
  ChatBubble as ChatBubbleIcon,
  SettingsSuggest as SettingsSuggestIcon,
  People as PeopleIcon,
} from "@mui/icons-material";
import { useProjects } from "../contexts/ProjectContext";
import { useOrganization } from "../contexts/OrganizationContext";
import { useAuth } from "../contexts/AuthContext";
import DashboardLayout from "../components/DashboardLayout";
import CreateProjectForm from "../components/CreateProjectForm";
import { projectsApi } from "../services/rag-service";
import { organizationApi } from "../services/organization-service";
import { useTranslation } from "react-i18next";
import { getIntlLocale } from "../utils/language";

const getAccessLevels = (t) => [
  {
    value: "admin",
    label: t("aiCatalog.admin"),
    icon: (
      <AdminPanelSettingsIcon
        fontSize="small"
        sx={{ color: "var(--color-79)" }}
      />
    ),
  },
  {
    value: "modifier",
    label: t("aiCatalog.editor"),
    icon: <EditorIcon fontSize="small" sx={{ color: "var(--color-80)" }} />,
  },
  {
    value: "viewer",
    label: t("aiCatalog.viewer"),
    icon: <ViewerIcon fontSize="small" sx={{ color: "var(--color-81)" }} />,
  },
];

// Function to map API access levels to UI display
const mapApiAccessLevelToUI = (apiAccessLevel) => {
  if (apiAccessLevel === "modifier") {
    return "editor";
  }
  return apiAccessLevel;
};

// Function to map UI access levels to API
const mapUIAccessLevelToAPI = (uiAccessLevel) => {
  if (uiAccessLevel === "editor") {
    return "modifier";
  }
  return uiAccessLevel;
};

const validateEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
};

// Access level chip styling
const getAccessLevelChip = (level, t) => {
  // Transform API level to UI level for display
  const uiLevel = mapApiAccessLevelToUI(level);
  const accessLevels = getAccessLevels(t);
  const accessLevel =
    accessLevels.find((l) => l.value === level) ||
    accessLevels.find((l) => l.value === uiLevel);

  let color = "default";
  if (level === "admin") color = "success";
  else if (level === "modifier" || level === "editor") color = "primary";
  else if (level === "viewer") color = "default";

  return (
    <Chip
      icon={accessLevel?.icon}
      label={accessLevel?.label || level}
      size="small"
      color={color}
      variant="outlined"
      sx={{
        fontWeight: 500,
        borderRadius: 1,
      }}
    />
  );
};

const ProjectsManagement = () => {
  const {
    availableProjects,
    deleteProject,
    updateProject,
    shareProject,
    updateShareAccess,
  } = useProjects();
  const { currentOrganization } = useOrganization();
  const { currentUser } = useAuth();
  const isOrgOwner = currentOrganization?.role_id === "owner";
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";
  const accessLevels = getAccessLevels(t);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProject, setSelectedProject] = useState(null);
  const [createFormOpen, setCreateFormOpen] = useState(false);

  // Emails and user ids of the current organization's owners
  const [orgOwnerIds, setOrgOwnerIds] = useState(new Set());

  // Project members state
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState("");
  const [membersPagination, setMembersPagination] = useState({
    page: 1,
    size: 10,
    total: 0,
    pages: 1,
  });
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // New state for dependency checking
  const [dependenciesLoading, setDependenciesLoading] = useState(false);
  const [dependencies, setDependencies] = useState(null);
  const [dependencyTypes, setDependencyTypes] = useState(null);
  const [forceDelete, setForceDelete] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  // Member management states
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberDialogOpen, setMemberDialogOpen] = useState(false);
  const [accessLevelDialogOpen, setAccessLevelDialogOpen] = useState(false);
  const [newAccessLevel, setNewAccessLevel] = useState("");
  const [newMemberEmail, setNewMemberEmail] = useState("");
  const [newMemberAccessLevel, setNewMemberAccessLevel] = useState("viewer");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // New state for editing project
  const [editProjectDialogOpen, setEditProjectDialogOpen] = useState(false);
  const [projectToEdit, setProjectToEdit] = useState(null);
  const [editProjectName, setEditProjectName] = useState("");
  const [editProjectDescription, setEditProjectDescription] = useState("");

  const [tab, setTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  useEffect(() => {
    if (dependencies === null) return;

    const dependencyTypes = Object.entries(dependencies).filter(
      ([key, value]) => value?.count > 0,
    );

    setDependencyTypes(dependencyTypes);
    setTab(0);
  }, [dependencies]);

  // Function to fetch project dependencies
  const fetchProjectDependencies = useCallback(async (projectId) => {
    if (!projectId) return null;

    setDependenciesLoading(true);
    try {
      const deps = await projectsApi.getProjectDependencies(projectId);
      setDependencies(deps);
      return deps;
    } catch (error) {
      console.error("Error fetching project dependencies:", error);
      // Return empty dependencies object on error so deletion can proceed
      const emptyDeps = {
        has_dependencies: false,
        total_count: 0,
        collections: { count: 0, items: [] },
        flows: { count: 0, items: [] },
        llm_configs: { count: 0, items: [] },
        conversations: { count: 0, items: [] },
      };
      setDependencies(emptyDeps);
      return emptyDeps;
    } finally {
      setDependenciesLoading(false);
    }
  }, []);

  // Function to fetch project members with pagination
  const fetchProjectMembers = useCallback(async () => {
    if (!selectedProject) return;

    setMembersLoading(true);
    setMembersError("");

    try {
      const result = await projectsApi.getProjectMembers(selectedProject.id, {
        page: membersPagination.page,
        limit: membersPagination.size || 10,
      });

      setMembers(result.items || []);
      setMembersPagination((prev) => ({
        page: result.page || prev.page || 1,
        total: result.total || 0,
        pages: result.pages || 1,
        size: 10,
      }));
    } catch (error) {
      console.error("Error fetching project members:", error);
      setMembersError(error.message || "Failed to load team members");
      setMembers([]);
    } finally {
      setMembersLoading(false);
    }
  }, [selectedProject, membersPagination.page, membersPagination.size]);

  // Fetch the organization's owners so their project rows can be protected
  useEffect(() => {
    const fetchOrgOwners = async () => {
      if (!currentOrganization?.id) {
        setOrgOwnerIds(new Set());
        return;
      }
      try {
        const response = await organizationApi.getOrganizationMembers(
          currentOrganization.id,
        );
        const ids = new Set();
        (response.data || []).forEach((member) => {
          if (member.role_id === "owner") {
            if (member.user_id) ids.add(member.user_id);
            if (member.user?.email) ids.add(member.user.email);
          }
        });
        setOrgOwnerIds(ids);
      } catch (error) {
        console.error("Error fetching organization members:", error);
      }
    };
    fetchOrgOwners();
  }, [currentOrganization?.id]);

  const isMemberOrgOwner = (member) =>
    orgOwnerIds.has(member.user_id) || orgOwnerIds.has(member.email);

  // Fetch project members when a project is selected
  useEffect(() => {
    if (selectedProject) {
      fetchProjectMembers();
    }
  }, [selectedProject, membersPagination.page, fetchProjectMembers]);

  const handlePageChange = (event, newPage) => {
    setMembersPagination((prev) => ({
      ...prev,
      page: newPage,
    }));
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleCreateProject = () => {
    setCreateFormOpen(true);
  };

  const handleCreateFormClose = () => {
    setCreateFormOpen(false);
  };

  const handleProjectClick = (project) => {
    setSelectedProject(project);
    // Reset pagination and search when viewing a project
    setMembersPagination({
      page: 1,
      size: 10,
      total: 0,
      pages: 1,
    });
    setSearchQuery("");
  };

  const handleBackToProjects = () => {
    setSelectedProject(null);
  };

  const handleEditProject = (e, project) => {
    e.stopPropagation();
    setProjectToEdit(project);
    setEditProjectName(project.name);
    setEditProjectDescription(project.description || "");
    setEditProjectDialogOpen(true);
  };

  const handleEditProjectSave = async () => {
    try {
      await updateProject(projectToEdit.id, {
        name: editProjectName,
        description: editProjectDescription,
      });
      setEditProjectDialogOpen(false);
      setProjectToEdit(null);
    } catch (error) {
      console.error("Error updating project:", error);
    }
  };

  const handleEditProjectClose = () => {
    setEditProjectDialogOpen(false);
    setProjectToEdit(null);
  };

  // Enhanced delete project handler with dependency checking
  const handleDeleteProject = async (e, project) => {
    e.stopPropagation();
    setProjectToDelete(project);
    setForceDelete(false);

    // Fetch dependencies before showing the dialog
    try {
      setDeleteId(project.id);
      await fetchProjectDependencies(project.id);
      setDeleteId(null);
      setDeleteConfirmOpen(true);
    } catch (error) {
      console.error("Failed to fetch project dependencies:", error);
      setErrorMessage(error.message || "Failed to fetch project dependencies");
    }
  };

  const handleConfirmProjectDelete = async () => {
    if (!projectToDelete) return;

    setIsSubmitting(true);
    try {
      await deleteProject(projectToDelete.id, false, forceDelete);
      setDeleteConfirmOpen(false);
      setProjectToDelete(null);
      setDependencies(null);

      // If the deleted project was selected, go back to projects list
      if (selectedProject && selectedProject.id === projectToDelete.id) {
        setSelectedProject(null);
      }
    } catch (error) {
      console.error("Error deleting project:", error);
      setErrorMessage(error.message || "Failed to delete project");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelProjectDelete = () => {
    setDeleteConfirmOpen(false);
    setProjectToDelete(null);
    setDependencies(null);
    setForceDelete(false);
    setErrorMessage("");
  };

  // Member management handlers
  const handleMenuOpen = (event, member) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedMember(member);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedMember(null);
  };

  const handleAddMember = () => {
    setMemberDialogOpen(true);
  };

  const handleMemberDialogClose = () => {
    setMemberDialogOpen(false);
    setNewMemberEmail("");
    setNewMemberAccessLevel("viewer");
    setErrorMessage("");
  };

  const handleChangeAccessLevel = () => {
    setMenuAnchorEl(null);
    // Convert API level to UI level for the form
    const uiAccessLevel = mapApiAccessLevelToUI(selectedMember.access_level);
    setNewAccessLevel(uiAccessLevel);
    setAccessLevelDialogOpen(true);
  };

  const handleAccessLevelDialogClose = () => {
    setAccessLevelDialogOpen(false);
    setErrorMessage("");
  };

  const handleAccessLevelChange = (event) => {
    setNewAccessLevel(event.target.value);
  };

  const handleAccessLevelSave = async () => {
    if (!selectedMember) return;

    setIsSubmitting(true);
    setErrorMessage("");

    try {
      // Map editor to modifier for the API call
      const apiAccessLevel = mapUIAccessLevelToAPI(newAccessLevel);

      // Use shareId if available, otherwise use user_id
      const shareId = selectedMember.id || selectedMember.user_id;

      await updateShareAccess(selectedProject.id, shareId, apiAccessLevel);

      // Refresh the members list after updating
      fetchProjectMembers();

      setAccessLevelDialogOpen(false);
    } catch (error) {
      console.error("Error updating access level:", error);
      setErrorMessage(error.message || "Failed to update access level");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteMember = () => {
    setMenuAnchorEl(null);

    // Skip if no selected member or project
    if (!selectedMember || !selectedProject) return;

    // Show confirmation dialog before deleting
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDeleteMember = async () => {
    setIsSubmitting(true);
    setErrorMessage("");

    try {
      // Use shareId if available, otherwise use user_id
      const shareId = selectedMember.id || selectedMember.user_id;

      // Call API to remove share
      await projectsApi.deleteProjectShare(selectedProject.id, shareId);

      // Refresh members list after removing
      fetchProjectMembers();

      // Close dialogs
      setDeleteConfirmOpen(false);
      setSelectedMember(null);
    } catch (error) {
      console.error("Error removing team member:", error);
      setErrorMessage(error.message || "Failed to remove team member");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddNewMember = async () => {
    const trimmedEmail = newMemberEmail.trim();

    if (!trimmedEmail) {
      setErrorMessage(t("shareDialog.emailRequired"));
      return;
    }

    if (!validateEmail(trimmedEmail)) {
      setErrorMessage(t("shareDialog.validEmailRequired"));
      return;
    }

    setIsSubmitting(true);
    setErrorMessage("");

    try {
      // Map editor to modifier for the API call
      const apiAccessLevel = mapUIAccessLevelToAPI(newMemberAccessLevel);

      // Call the shareProject API with separate parameters for email and accessLevel
      // NOT passing a single object with email and access_level properties
      await shareProject(selectedProject.id, trimmedEmail, apiAccessLevel);

      // Refresh members after adding
      fetchProjectMembers();

      // Close the dialog
      handleMemberDialogClose();
    } catch (error) {
      console.error("Error sharing project:", error);
      setErrorMessage(error.message || "Failed to add team member");
    } finally {
      setIsSubmitting(false);
    }
  };

  const StyledTab = styled(Tab)(({ theme }) => ({
    textTransform: "none",
    minHeight: 48,
    fontWeight: 500,
    color: theme.palette.text.secondary,
    "&.Mui-selected": {
      color: theme.palette.success.main,
      border: theme.palette.action.selected,
      borderBottom: `2px solid ${theme.palette.success.main}`,
    },
    "& .MuiTab-iconWrapper": {
      marginInlineEnd: theme.spacing(1),
    },
  }));

  const typeIcons = {
    collections: <LibraryBooksIcon />,
    flows: <AccountTreeIcon />,
    conversations: <ChatBubbleIcon />,
    llm_configs: <SettingsSuggestIcon />,
    shared_users: <PeopleIcon />,
  };

  const formatLabel = (key) => {
    const labels = {
      collections: t("projects.collections"),
      flows: t("projects.flows"),
      llm_configs: t("projects.llmConfigurations"),
      conversations: t("projects.conversations"),
      shared_users: t("projects.sharedUsers"),
    };
    return labels[key] || key;
  };

  function formatDate(isoString) {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return "";
    const lang = i18n.language || "en";
    return date.toLocaleString(getIntlLocale(lang), {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  }

  // Filter projects based on search query
  const filteredProjects = availableProjects.filter((project) =>
    project.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  // Filter members based on search query
  const filteredMembers =
    searchQuery && members.length > 0
      ? members.filter(
          (member) =>
            (member.email &&
              member.email.toLowerCase().includes(searchQuery.toLowerCase())) ||
            (member.first_name &&
              member.first_name
                .toLowerCase()
                .includes(searchQuery.toLowerCase())) ||
            (member.last_name &&
              member.last_name
                .toLowerCase()
                .includes(searchQuery.toLowerCase())) ||
            (member.username &&
              member.username
                .toLowerCase()
                .includes(searchQuery.toLowerCase())),
        )
      : members;

  return (
    <DashboardLayout title={t("projects.title")}>
      <Box
        sx={{ display: "flex", flexDirection: "column", height: "100%", mt: 2 }}
      >
        {/* Header Section */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {selectedProject && (
              <IconButton
                sx={{ marginInlineEnd: 1 }}
                onClick={handleBackToProjects}
                aria-label="back to projects list"
              >
                {isRTL ? <ArrowForwardIcon /> : <ArrowBackIcon />}
              </IconButton>
            )}
            <Typography variant="h5" fontWeight={600}>
              {selectedProject
                ? t("projects.projectLabel", { name: selectedProject.name })
                : t("projects.title")}
            </Typography>
          </Box>

          <Box>
            {!selectedProject ? (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleCreateProject}
                sx={{ borderRadius: 1 }}
              >
                {t("projects.createProject")}
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                startIcon={<PersonAddIcon />}
                onClick={handleAddMember}
                sx={{ borderRadius: 1 }}
              >
                {t("projects.addTeamMember")}
              </Button>
            )}
          </Box>
        </Box>

        {/* Search and Filter Bar */}
        <Box sx={{ mb: 3, display: "flex", alignItems: "center" }}>
          <TextField
            placeholder={
              selectedProject
                ? t("projects.searchMembers")
                : t("projects.searchProjects")
            }
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={handleSearchChange}
            sx={{
              width: 300,
              "& .MuiOutlinedInput-root": {
                borderRadius: 1,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ marginInlineStart: "auto" }}>
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              sx={{
                marginInlineStart: 1,
                borderRadius: 1,
                borderColor: "divider",
                color: "text.primary",
              }}
            >
              {t("common.filter")}
            </Button>
          </Box>
        </Box>

        {/* Content Area */}
        <Paper
          sx={{
            flexGrow: 1,
            borderRadius: 1,
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "none",
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Projects List View (when no project is selected) */}
          {!selectedProject ? (
            <TableContainer sx={{ flexGrow: 1 }}>
              <Table sx={{ minWidth: 650 }}>
                <TableHead sx={{ backgroundColor: "background.paperFaded" }}>
                  <TableRow>
                    <TableCell align={isRTL ? "right" : "left"}>
                      {t("projects.projectName")}
                    </TableCell>
                    <TableCell align={isRTL ? "right" : "left"}>
                      {t("common.description")}
                    </TableCell>
                    <TableCell align={isRTL ? "right" : "left"}>
                      {t("projects.members")}
                    </TableCell>
                    <TableCell align={isRTL ? "right" : "left"}>
                      {t("common.created")}
                    </TableCell>
                    <TableCell align={isRTL ? "right" : "left"}>
                      {t("common.actions")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredProjects.map((project) => (
                    <TableRow
                      key={project.id}
                      sx={{
                        "&:hover": {
                          backgroundColor: "action.hover",
                          cursor: "pointer",
                        },
                      }}
                      onClick={() => handleProjectClick(project)}
                    >
                      <TableCell align={isRTL ? "right" : "left"}>
                        {project.name}
                      </TableCell>
                      <TableCell align={isRTL ? "right" : "left"}>
                        {project.description ||
                          t("projects.noDescriptionAvailable")}
                      </TableCell>
                      <TableCell align={isRTL ? "right" : "left"}>
                        {/* We no longer have accurate count without fetching, so use a placeholder */}
                        - {t("projects.members")}
                      </TableCell>
                      <TableCell align={isRTL ? "right" : "left"}>
                        {project.createdAt
                          ? new Date(project.createdAt).toLocaleDateString()
                          : "N/A"}
                      </TableCell>
                      <TableCell align={isRTL ? "right" : "left"}>
                        {(project.isOwner ||
                          project.accessLevel === "admin" ||
                          project.accessLevel === "owner") && (
                          <>
                            <Tooltip title={t("projects.editProject")}>
                              <IconButton
                                size="small"
                                onClick={(e) => handleEditProject(e, project)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t("projects.deleteProject")}>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={(e) => handleDeleteProject(e, project)}
                              >
                                {deleteId === project.id ? (
                                  <CircularProgress size={16} color="inherit" />
                                ) : (
                                  <DeleteIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredProjects.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                        <Typography variant="body1" color="text.secondary">
                          {t("projects.noProjectsFound")}
                        </Typography>
                        <Button
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={handleCreateProject}
                          sx={{ mt: 2, borderRadius: 1 }}
                        >
                          {t("projects.createNewProject")}
                        </Button>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            // Project Detail View - Members Table
            <>
              <TableContainer sx={{ flexGrow: 1 }}>
                <Table sx={{ minWidth: 650 }}>
                  <TableHead sx={{ backgroundColor: "background.paperFaded" }}>
                    <TableRow>
                      <TableCell align={isRTL ? "right" : "left"}>
                        {t("common.name")}
                      </TableCell>
                      <TableCell align={isRTL ? "right" : "left"}>
                        {t("aiCatalog.email")}
                      </TableCell>
                      <TableCell align={isRTL ? "right" : "left"}>
                        {t("settings.roles")}
                      </TableCell>
                      <TableCell align={isRTL ? "right" : "left"}>
                        {t("common.created")}
                      </TableCell>
                      <TableCell align={isRTL ? "right" : "left"}>
                        {t("common.actions")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {membersLoading ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                          <CircularProgress size={32} />
                          <Typography sx={{ marginInlineStart: 2 }}>
                            Loading team members...
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : membersError ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                          <Alert
                            severity="error"
                            sx={{ justifyContent: "center" }}
                          >
                            {membersError}
                          </Alert>
                          <Button
                            variant="outlined"
                            onClick={fetchProjectMembers}
                            sx={{ mt: 2, borderRadius: 1 }}
                          >
                            Retry
                          </Button>
                        </TableCell>
                      </TableRow>
                    ) : filteredMembers.length > 0 ? (
                      filteredMembers.map((member) => (
                        <TableRow key={member.user_id}>
                          <TableCell align={isRTL ? "right" : "left"}>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <Typography>
                                {member.first_name && member.last_name
                                  ? `${member.first_name} ${member.last_name}`
                                  : member.username || t("common.unknown")}
                                {member.is_owner && (
                                  <Chip
                                    label={t("organizationMembers.owner")}
                                    size="small"
                                    color="primary"
                                    sx={{
                                      marginInlineStart: 1,
                                      height: 20,
                                      fontSize: "0.7rem",
                                    }}
                                  />
                                )}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align={isRTL ? "right" : "left"}>
                            {member.email}
                          </TableCell>
                          <TableCell align={isRTL ? "right" : "left"}>
                            {getAccessLevelChip(member.access_level, t)}
                          </TableCell>
                          <TableCell align={isRTL ? "right" : "left"}>
                            {member.joined_at
                              ? new Date(member.joined_at).toLocaleDateString(
                                  getIntlLocale(i18n.language),
                                  {
                                    year: "numeric",
                                    month: "short",
                                    day: "numeric",
                                  },
                                )
                              : t("common.never")}
                          </TableCell>
                          <TableCell align={isRTL ? "right" : "left"}>
                            {member.email !== currentUser?.email &&
                              !isMemberOrgOwner(member) &&
                              (selectedProject.accessLevel === "owner" ||
                                selectedProject.accessLevel === "admin" ||
                                isOrgOwner) && (
                                <IconButton
                                  aria-label={t("common.actions")}
                                  onClick={(e) => handleMenuOpen(e, member)}
                                >
                                  <MoreVertIcon fontSize="small" />
                                </IconButton>
                              )}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                          <Typography variant="body1" color="text.secondary">
                            {searchQuery
                              ? t("projects.noProjectsFound")
                              : t("projects.noProjectsFound")}
                          </Typography>
                          <Button
                            variant="outlined"
                            startIcon={<PersonAddIcon />}
                            onClick={handleAddMember}
                            sx={{ mt: 2, borderRadius: 1 }}
                          >
                            {t("projects.addTeamMember")}
                          </Button>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination for Members */}
              {!membersLoading &&
                !membersError &&
                membersPagination.total > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      p: 2,
                      borderTop: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Box sx={{ marginInlineEnd: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t("projects.showingMembers", {
                          current: filteredMembers.length,
                          total: membersPagination.total,
                        })}
                      </Typography>
                    </Box>

                    <Pagination
                      count={membersPagination.pages}
                      page={membersPagination.page}
                      onChange={handlePageChange}
                      color="primary"
                      shape="rounded"
                      size="small"
                    />
                  </Box>
                )}
            </>
          )}
        </Paper>
      </Box>

      {/* Member Options Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            minWidth: 180,
            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
            borderRadius: 1,
            border: "1px solid",
            borderColor: "divider",
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem onClick={handleChangeAccessLevel}>
          <ListItemIcon>
            <AdminPanelSettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("projects.changeAccessLevel")}</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleDeleteMember} sx={{ color: "error.main" }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>{t("projects.removeMember")}</ListItemText>
        </MenuItem>
      </Menu>
      <Dialog
        open={deleteConfirmOpen && projectToDelete && !selectedMember}
        onClose={handleCancelProjectDelete}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 1,
            maxHeight: "80vh",
          },
        }}
      >
        <DialogTitle
          sx={{ pb: 1, display: "flex", alignItems: "center", gap: 1 }}
        >
          <WarningIcon color="warning" />
          <Typography variant="h6" fontWeight={600}>
            {t("projects.deleteProjectTitle")}
          </Typography>
        </DialogTitle>
        <Divider />

        <DialogContent sx={{ pt: 3 }}>
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 1 }}>
              {errorMessage}
            </Alert>
          )}

          {dependenciesLoading ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                py: 3,
              }}
            >
              <CircularProgress size={24} />
              <Typography sx={{ marginInlineStart: 2 }}>
                {t("projects.checkingDependencies")}
              </Typography>
            </Box>
          ) : dependencies ? (
            <>
              {!dependencies.has_dependencies ? (
                // No dependencies - simple confirmation
                <>
                  <Alert
                    severity="info"
                    sx={{
                      mb: 2,
                      borderRadius: "0.625rem",
                      border: "1px solid var(--color-3)29",
                      background: "var(--color-82)",
                    }}
                  >
                    {t("projects.noDependencies")}
                  </Alert>
                  <Typography variant="body1">
                    {t("projects.deleteProjectConfirm", {
                      name: projectToDelete?.name,
                    })}
                  </Typography>
                </>
              ) : dependencyTypes && dependencyTypes.length ? (
                // Has dependencies - show detailed information
                <Box>
                  <Alert
                    severity="warning"
                    sx={{
                      mb: 2,
                      borderRadius: "0.625rem",
                      border: "1px solid var(--color-3)29",
                      background: "var(--color-83)",
                      fontSize: "0.875rem",
                      color: "var(--color-84)",
                    }}
                  >
                    {t("projects.hasDependencies", {
                      count: dependencies.total_count,
                    })}
                  </Alert>

                  <Tabs
                    value={tab}
                    onChange={handleTabChange}
                    textColor="inherit"
                    TabIndicatorProps={{ style: { display: "none" } }} // hide underline
                  >
                    {dependencyTypes.map(([key, value], index) => (
                      <StyledTab
                        key={key}
                        icon={typeIcons[key] || null}
                        iconPosition="start"
                        label={
                          <Box display="flex" alignItems="center" gap={2}>
                            <Typography>{formatLabel(key)}</Typography>
                            <Badge
                              badgeContent={value.count}
                              sx={{
                                "& .MuiBadge-badge": {
                                  borderRadius: "50%",
                                  background:
                                    tab === index
                                      ? "var(--color-85)"
                                      : "var(--color-86)",
                                  color: "var(--color-3)",
                                  fontSize: "0.75rem",
                                  minWidth: 20,
                                  height: 20,
                                },
                              }}
                            />
                          </Box>
                        }
                      />
                    ))}
                  </Tabs>

                  <Grid container spacing={2} sx={{ mt: 1, mb: 1 }}>
                    {dependencyTypes[tab][1].items.map((item) => (
                      <Grid item xs={12} sm={6} md={4} key={item.id}>
                        <Card
                          variant="outlined"
                          sx={{
                            bgcolor: "background.paper",
                            position: "relative",
                            borderRadius: "0.5rem",
                            border: "1px solid var(--color-3)1f",
                            p: 2,
                          }}
                        >
                          {item.name ? (
                            <Typography variant="subtitle1" fontWeight="bold">
                              {item.name}
                            </Typography>
                          ) : null}
                          {item.description ? (
                            <Typography variant="body2" color="text.secondary">
                              {item.description}
                            </Typography>
                          ) : null}
                          {item.updated_at ? (
                            <Typography variant="body2" color="text.secondary">
                              {t("projects.lastEdited")}:{" "}
                              {formatDate(item.updated_at)}
                            </Typography>
                          ) : null}
                        </Card>
                      </Grid>
                    ))}
                  </Grid>

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={forceDelete}
                        onChange={(e) => setForceDelete(e.target.checked)}
                        color="error"
                      />
                    }
                    label={
                      <Typography color="error">
                        {t("projects.understandDeleteWarning", {
                          count: dependencies.total_count,
                        })}
                      </Typography>
                    }
                    sx={{ mt: 2 }}
                  />
                </Box>
              ) : null}
            </>
          ) : (
            // Error state or no dependencies data
            <Alert severity="error" sx={{ mb: 2, borderRadius: 1 }}>
              {t("projects.unableToCheckDeps")}
            </Alert>
          )}
        </DialogContent>

        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleCancelProjectDelete}
            color="secondary"
            variant="text"
            disabled={isSubmitting}
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleConfirmProjectDelete}
            color="error"
            variant="contained"
            sx={{ borderRadius: 1 }}
            disabled={
              isSubmitting ||
              dependenciesLoading ||
              (dependencies?.has_dependencies && !forceDelete)
            }
            startIcon={
              isSubmitting ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <DeleteIcon />
              )
            }
          >
            {isSubmitting
              ? t("projects.deleting")
              : dependencies?.has_dependencies
                ? t("projects.deleteProjectAndDeps")
                : t("projects.deleteProject")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Access Level Change Dialog */}
      <Dialog
        open={accessLevelDialogOpen}
        onClose={handleAccessLevelDialogClose}
        PaperProps={{
          sx: {
            borderRadius: 1,
            width: 400,
            maxWidth: "95vw",
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight={600}>
            {t("projects.changeAccessLevel")}
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}

          <Typography variant="body2" sx={{ mb: 2 }}>
            {t("projects.selectAccessLevelForUser", {
              name:
                selectedMember?.first_name && selectedMember?.last_name
                  ? `${selectedMember.first_name} ${selectedMember.last_name}`
                  : selectedMember?.email || t("projects.thisUser"),
            })}
          </Typography>
          <FormControl fullWidth variant="outlined" size="small" sx={{ mb: 2 }}>
            <InputLabel id="access-level-label">
              {t("projects.accessLevel")}
            </InputLabel>
            <Select
              labelId="access-level-label"
              id="access-level-select"
              value={newAccessLevel}
              label={t("projects.accessLevel")}
              onChange={handleAccessLevelChange}
              sx={{
                borderRadius: 1,
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                },
              }}
              disabled={isSubmitting}
            >
              {accessLevels.map((level) => (
                <MenuItem
                  key={level.value}
                  value={level.value}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    py: 1.5,
                  }}
                >
                  {level.icon}
                  <Typography>{level.label}</Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {["admin", "editor", "viewer"].includes(newAccessLevel) && (
            <Box sx={{ mt: 2 }}>
              <Alert
                severity="info"
                sx={{
                  borderRadius: 1,
                  "& .MuiAlert-message": {
                    width: "100%",
                  },
                }}
                icon={
                  newAccessLevel === "admin" ? (
                    <AdminPanelSettingsIcon />
                  ) : newAccessLevel === "editor" ? (
                    <EditorIcon />
                  ) : newAccessLevel === "viewer" ? (
                    <ViewerIcon />
                  ) : null
                }
              >
                <Typography variant="body2">
                  {newAccessLevel === "admin" &&
                    t("projects.adminAccessDescription")}
                  {newAccessLevel === "editor" &&
                    t("projects.editAccessDescription")}
                  {newAccessLevel === "viewer" &&
                    t("projects.viewerAccessDescription")}
                </Typography>
              </Alert>
            </Box>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleAccessLevelDialogClose}
            color="secondary"
            variant="text"
            disabled={isSubmitting}
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleAccessLevelSave}
            variant="contained"
            sx={{ borderRadius: 1 }}
            disabled={isSubmitting}
            startIcon={
              isSubmitting ? (
                <CircularProgress size={20} color="inherit" />
              ) : null
            }
          >
            {isSubmitting ? t("common.saving") : t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Member Dialog */}
      <Dialog
        open={memberDialogOpen}
        onClose={handleMemberDialogClose}
        PaperProps={{
          sx: {
            borderRadius: 1,
            width: 400,
            maxWidth: "95vw",
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight={600}>
            {t("projects.addTeamMember")}
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            id="email"
            label={t("common.emailAddress")}
            type="email"
            fullWidth
            variant="outlined"
            value={newMemberEmail}
            onChange={(e) => {
              setNewMemberEmail(e.target.value);
              if (errorMessage) {
                setErrorMessage("");
              }
            }}
            sx={{ mb: 2 }}
            error={!!errorMessage}
            helperText={errorMessage}
            disabled={isSubmitting}
          />
          <FormControl fullWidth variant="outlined" size="small">
            <InputLabel id="new-access-level-label">
              {t("projects.accessLevel")}
            </InputLabel>
            <Select
              labelId="new-access-level-label"
              id="new-access-level-select"
              value={newMemberAccessLevel}
              label={t("projects.accessLevel")}
              onChange={(e) => setNewMemberAccessLevel(e.target.value)}
              sx={{
                borderRadius: 1,
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                },
              }}
              disabled={isSubmitting}
            >
              {accessLevels.map((level) => (
                <MenuItem
                  key={level.value}
                  value={level.value}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    py: 1.5,
                  }}
                >
                  {level.icon}
                  <Typography>{level.label}</Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleMemberDialogClose}
            color="secondary"
            variant="text"
            disabled={isSubmitting}
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleAddNewMember}
            variant="contained"
            sx={{ borderRadius: 1 }}
            disabled={!newMemberEmail.trim() || isSubmitting}
            startIcon={
              isSubmitting ? (
                <CircularProgress size={20} color="inherit" />
              ) : null
            }
          >
            {isSubmitting ? t("common.adding") : t("projects.addNewMember")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Project Dialog */}
      <Dialog
        open={editProjectDialogOpen}
        onClose={handleEditProjectClose}
        PaperProps={{
          sx: {
            borderRadius: 1,
            width: 500,
            maxWidth: "95vw",
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight={600}>
            {t("projects.editProject")}
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <TextField
            autoFocus
            margin="dense"
            id="name"
            label={t("projects.projectName")}
            type="text"
            fullWidth
            variant="outlined"
            value={editProjectName}
            onChange={(e) => setEditProjectName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            id="description"
            label={t("common.description")}
            type="text"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={editProjectDescription}
            onChange={(e) => setEditProjectDescription(e.target.value)}
          />
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleEditProjectClose}
            color="secondary"
            variant="text"
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleEditProjectSave}
            variant="contained"
            sx={{ borderRadius: 1 }}
            disabled={!editProjectName.trim()}
          >
            {t("common.saveChanges")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Member Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen && !projectToDelete && selectedMember}
        onClose={() => setDeleteConfirmOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 1,
          },
        }}
      >
        <DialogTitle>{t("projects.confirmMemberRemoval")}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 1 }}>
            {t("projects.revokeMemberAccessWarning")}
          </Alert>
          <Typography variant="body1">
            {t("projects.removeMemberConfirm", {
              name:
                selectedMember?.first_name && selectedMember?.last_name
                  ? `${selectedMember.first_name} ${selectedMember.last_name}`
                  : selectedMember?.email || t("projects.thisUser"),
            })}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeleteConfirmOpen(false)}
            color="secondary"
            variant="text"
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleConfirmDeleteMember}
            color="error"
            variant="contained"
            sx={{ borderRadius: 1 }}
            disabled={isSubmitting}
            startIcon={
              isSubmitting ? (
                <CircularProgress size={20} color="inherit" />
              ) : null
            }
          >
            {isSubmitting ? t("common.removing") : t("common.remove")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Project Form Dialog */}
      <CreateProjectForm
        open={createFormOpen}
        onClose={handleCreateFormClose}
      />
    </DashboardLayout>
  );
};

export default ProjectsManagement;
